import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, FileText, Grid, Settings, Users, Plus, Edit, Trash2, Eye, Search,
  ChevronLeft, Loader2, Download, Check, X, Image as ImageIcon, 
  CreditCard, AlertCircle, Save, Percent, Building, LogOut, MessageSquare,
  CheckCircle, XCircle, RefreshCw, Mail, Phone, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Customers Management with Verification Status
export const AdminCustomers = () => {
  const { user, getAuthHeaders, loading: userLoading } = useApp();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (userLoading) return; // Wait for user to load
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchCustomers();
  }, [user, userLoading]);

  const fetchCustomers = async () => {
    try { 
      const res = await axios.get(`${API}/admin/customers`, { headers: getAuthHeaders() }); 
      setCustomers(res.data.customers); 
    } catch (e) { toast.error('Failed to load customers'); }
    setLoading(false);
  };

  const handleVerifyEmail = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/verify-email`, {}, { headers: getAuthHeaders() });
      toast.success('Email verified manually');
      fetchCustomers();
    } catch (e) { toast.error('Failed to verify email'); }
    setActionLoading(null);
  };

  const handleResendOTP = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/resend-otp`, {}, { headers: getAuthHeaders() });
      toast.success('OTP sent to customer');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send OTP'); }
    setActionLoading(null);
  };

  const handleToggleStatus = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/toggle-status`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      fetchCustomers();
    } catch (e) { toast.error('Failed to update status'); }
    setActionLoading(null);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-muted-foreground hover:text-gold"><ChevronLeft className="h-5 w-5" /></Link>
          <h1 className="text-2xl font-bold text-navy">Customers</h1>
          <Badge variant="secondary">{customers.length} total</Badge>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-center">Email Verified</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell className="font-medium">{customer.name}</TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.phone || '-'}</TableCell>
                  <TableCell className="text-center">
                    {customer.email_verified ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" /> Yes
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">
                        <XCircle className="h-3 w-3 mr-1" /> No
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className={customer.is_active !== false ? 'bg-green-500' : 'bg-gray-500'}>
                      {customer.is_active !== false ? 'Active' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {!customer.email_verified && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleVerifyEmail(customer.id)}
                            disabled={actionLoading === customer.id}
                            title="Manually verify email"
                          >
                            {actionLoading === customer.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Shield className="h-3 w-3" />}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleResendOTP(customer.id)}
                            disabled={actionLoading === customer.id}
                            title="Resend OTP"
                          >
                            {actionLoading === customer.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          </Button>
                        </>
                      )}
                      <Button 
                        size="sm" 
                        variant={customer.is_active !== false ? "destructive" : "default"}
                        onClick={() => handleToggleStatus(customer.id)}
                        disabled={actionLoading === customer.id}
                        title={customer.is_active !== false ? 'Disable user' : 'Enable user'}
                      >
                        {actionLoading === customer.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 
                          customer.is_active !== false ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />
                        }
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {customers.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">No customers found</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Products Management
export const AdminProducts = () => {
  const { user, getAuthHeaders, logout } = useApp();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', offer_price: '', category_id: '', sku: '', stock: '', brand: '', images: '', is_active: true, is_featured: false,
    highlights: '', specifications: '', box_contents: '', faqs: ''
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchProducts();
    fetchCategories();
  }, [user, navigate]);

  const fetchProducts = async () => {
    try { const res = await axios.get(`${API}/products/all`, { headers: getAuthHeaders() }); setProducts(res.data.products); } catch (e) { toast.error('Failed to load products'); }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try { const res = await axios.get(`${API}/categories/all`, { headers: getAuthHeaders() }); setCategories(res.data); } catch (e) { console.error('Failed to load categories'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Parse highlights (one per line)
    const highlights = formData.highlights ? formData.highlights.split('\n').map(s => s.trim()).filter(Boolean) : [];
    
    // Parse specifications (key: value per line)
    const specifications = {};
    if (formData.specifications) {
      formData.specifications.split('\n').forEach(line => {
        const [key, ...valueParts] = line.split(':');
        if (key && valueParts.length) {
          specifications[key.trim()] = valueParts.join(':').trim();
        }
      });
    }
    
    // Parse box contents (one per line)
    const box_contents = formData.box_contents ? formData.box_contents.split('\n').map(s => s.trim()).filter(Boolean) : [];
    
    // Parse FAQs (Q: and A: format)
    const faqs = [];
    if (formData.faqs) {
      const faqBlocks = formData.faqs.split(/\n\n+/);
      faqBlocks.forEach(block => {
        const qMatch = block.match(/Q:\s*(.+)/);
        const aMatch = block.match(/A:\s*(.+)/s);
        if (qMatch && aMatch) {
          faqs.push({ q: qMatch[1].trim(), a: aMatch[1].trim() });
        }
      });
    }
    
    const data = {
      ...formData,
      price: parseFloat(formData.price),
      offer_price: formData.offer_price ? parseFloat(formData.offer_price) : null,
      stock: parseInt(formData.stock) || 0,
      images: formData.images ? formData.images.split(',').map(s => s.trim()).filter(Boolean) : [],
      highlights,
      specifications,
      box_contents,
      faqs
    };
    
    // Remove string versions
    delete data.highlights;
    delete data.specifications;
    delete data.box_contents;
    delete data.faqs;
    data.highlights = highlights;
    data.specifications = specifications;
    data.box_contents = box_contents;
    data.faqs = faqs;
    
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, { headers: getAuthHeaders() });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, data, { headers: getAuthHeaders() });
        toast.success('Product created');
      }
      setShowForm(false); setEditingProduct(null); fetchProducts();
      setFormData({ name: '', description: '', price: '', offer_price: '', category_id: '', sku: '', stock: '', brand: '', images: '', is_active: true, is_featured: false, highlights: '', specifications: '', box_contents: '', faqs: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save product'); }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name, description: product.description, price: product.price?.toString(),
      offer_price: product.offer_price?.toString() || '', category_id: product.category_id,
      sku: product.sku, stock: product.stock?.toString(), brand: product.brand || '',
      images: product.images?.join(',') || '', is_active: product.is_active, is_featured: product.is_featured,
      highlights: product.highlights?.join('\n') || '',
      specifications: product.specifications ? Object.entries(product.specifications).map(([k,v]) => `${k}: ${v}`).join('\n') : '',
      box_contents: product.box_contents?.join('\n') || '',
      faqs: product.faqs ? product.faqs.map(f => `Q: ${f.q}\nA: ${f.a}`).join('\n\n') : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await axios.delete(`${API}/products/${id}`, { headers: getAuthHeaders() }); toast.success('Product deleted'); fetchProducts(); } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4"><Link to="/admin" className="text-muted-foreground hover:text-gold"><ChevronLeft className="h-5 w-5" /></Link><h1 className="text-2xl font-bold text-navy">Products Management</h1></div>
        <div className="flex items-center gap-3">
          <Button onClick={() => { setEditingProduct(null); setFormData({ name: '', description: '', price: '', offer_price: '', category_id: '', sku: '', stock: '', brand: '', images: '', is_active: true, is_featured: false, highlights: '', specifications: '', box_contents: '', faqs: '' }); setShowForm(true); }} className="bg-gold text-navy-dark"><Plus className="h-4 w-4 mr-2" />Add Product</Button>
          <Button variant="outline" onClick={() => { logout(); navigate('/'); }} className="text-red-500 border-red-200 hover:bg-red-50"><LogOut className="h-4 w-4 mr-2" />Sign Out</Button>
        </div>
      </div>

      {showForm ? (
        <Card className="mb-6">
          <CardHeader><CardTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="media">Images</TabsTrigger>
                  <TabsTrigger value="content">Content</TabsTrigger>
                  <TabsTrigger value="seo">SEO & More</TabsTrigger>
                </TabsList>
                
                {/* Basic Info Tab */}
                <TabsContent value="basic" className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Name *</Label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
                    <div className="space-y-2"><Label>SKU *</Label><Input required value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} /></div>
                    <div className="space-y-2 md:col-span-2"><Label>Description *</Label><Textarea required rows={4} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Price (AED) *</Label><Input type="number" step="0.01" required value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Offer Price (AED)</Label><Input type="number" step="0.01" value={formData.offer_price} onChange={(e) => setFormData({ ...formData, offer_price: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Category *</Label>
                      <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}><SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div className="space-y-2"><Label>Stock *</Label><Input type="number" required value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Brand</Label><Input value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} /></div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} /><Label>Active</Label></div>
                      <div className="flex items-center gap-2"><Switch checked={formData.is_featured} onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })} /><Label>Featured</Label></div>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Images Tab */}
                <TabsContent value="media" className="space-y-4">
                  <div className="border-2 border-dashed rounded-lg p-6 space-y-4">
                    <div className="flex items-center gap-4">
                      <input type="file" id="image-upload" accept="image/*" multiple className="hidden"
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (!files || files.length === 0) return;
                          const uploadFormData = new FormData();
                          for (let i = 0; i < files.length; i++) { uploadFormData.append('files', files[i]); }
                          try {
                            const res = await axios.post(`${API}/upload/images`, uploadFormData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                            const existingImages = formData.images ? formData.images.split(',').filter(Boolean) : [];
                            setFormData({ ...formData, images: [...existingImages, ...res.data.urls].join(',') });
                            toast.success(`${res.data.count} image(s) uploaded`);
                          } catch (err) { toast.error('Failed to upload images'); }
                          e.target.value = '';
                        }}
                      />
                      <Button type="button" variant="outline" onClick={() => document.getElementById('image-upload').click()}>
                        <ImageIcon className="h-4 w-4 mr-2" /> Upload Images
                      </Button>
                      <span className="text-sm text-muted-foreground">or</span>
                      <Input placeholder="Paste image URL and press Enter" className="flex-1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const url = e.target.value.trim();
                            if (url) {
                              const existingImages = formData.images ? formData.images.split(',').filter(Boolean) : [];
                              setFormData({ ...formData, images: [...existingImages, url].join(',') });
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                    </div>
                    {formData.images && (
                      <div className="flex flex-wrap gap-3">
                        {formData.images.split(',').filter(Boolean).map((img, idx) => (
                          <div key={idx} className="relative group">
                            <div className="w-24 h-24 rounded-lg overflow-hidden border bg-gray-50">
                              <img src={img.startsWith('/api') ? `${window.location.origin}${img}` : img} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" />
                            </div>
                            <button type="button" onClick={() => {
                              const images = formData.images.split(',').filter(Boolean);
                              images.splice(idx, 1);
                              setFormData({ ...formData, images: images.join(',') });
                            }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                {/* Content Tab - Editable Product Page Content */}
                <TabsContent value="content" className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Product Highlights</Label>
                    <p className="text-sm text-muted-foreground">One highlight per line. These appear as bullet points on the product page.</p>
                    <Textarea rows={6} placeholder="Premium quality materials&#10;Easy installation&#10;Water-efficient design&#10;1-year warranty included" value={formData.highlights} onChange={(e) => setFormData({ ...formData, highlights: e.target.value })} />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Technical Specifications</Label>
                    <p className="text-sm text-muted-foreground">Format: Key: Value (one per line)</p>
                    <Textarea rows={6} placeholder="Material: Premium Grade Ceramic&#10;Color: White&#10;Dimensions: 60 x 40 x 85 cm&#10;Weight: 25 kg" value={formData.specifications} onChange={(e) => setFormData({ ...formData, specifications: e.target.value })} />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">What's in the Box</Label>
                    <p className="text-sm text-muted-foreground">One item per line</p>
                    <Textarea rows={4} placeholder="Main product unit&#10;Mounting hardware&#10;Installation manual&#10;Warranty card" value={formData.box_contents} onChange={(e) => setFormData({ ...formData, box_contents: e.target.value })} />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">FAQs</Label>
                    <p className="text-sm text-muted-foreground">Format: Q: Question (newline) A: Answer (separate each FAQ with blank line)</p>
                    <Textarea rows={8} placeholder="Q: Is installation included?&#10;A: Installation service is available at additional cost.&#10;&#10;Q: What is the warranty period?&#10;A: This product comes with 1-year manufacturer warranty." value={formData.faqs} onChange={(e) => setFormData({ ...formData, faqs: e.target.value })} />
                  </div>
                </TabsContent>
                
                {/* SEO Tab */}
                <TabsContent value="seo" className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">SEO meta tags are automatically generated from product name and description. Additional SEO fields coming soon.</p>
                  </div>
                </TabsContent>
              </Tabs>
              
              <div className="flex gap-2 mt-6 pt-4 border-t">
                <Button type="submit" className="bg-gold text-navy-dark"><Save className="h-4 w-4 mr-2" />Save Product</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Image</TableHead><TableHead>Name</TableHead><TableHead>SKU</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id}>
                <TableCell><div className="w-12 h-12 rounded overflow-hidden bg-muted">{p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-full h-full p-2 text-muted-foreground" />}</div></TableCell>
                <TableCell><div className="font-medium">{p.name}</div><div className="text-sm text-muted-foreground">{p.brand}</div></TableCell>
                <TableCell>{p.sku}</TableCell>
                <TableCell><div className="font-bold text-gold">AED {p.offer_price || p.price}</div>{p.offer_price && <div className="text-sm line-through text-muted-foreground">AED {p.price}</div>}</TableCell>
                <TableCell><Badge className={p.stock > 10 ? 'bg-green-100 text-green-800' : p.stock > 0 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}>{p.stock}</Badge></TableCell>
                <TableCell><Badge className={p.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{p.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                <TableCell><div className="flex gap-1"><Button size="sm" variant="ghost" onClick={() => handleEdit(p)}><Edit className="h-4 w-4" /></Button><Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(p.id)}><Trash2 className="h-4 w-4" /></Button></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Admin Orders Management
export const AdminOrders = () => {
  const { user, getAuthHeaders } = useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try { const res = await axios.get(`${API}/orders/all`, { headers: getAuthHeaders() }); setOrders(res.data.orders); } catch (e) { toast.error('Failed to load orders'); }
    setLoading(false);
  };

  const updateStatus = async (orderId, status) => {
    try { await axios.put(`${API}/orders/${orderId}/status?status=${status}`, {}, { headers: getAuthHeaders() }); toast.success('Status updated'); fetchOrders(); } catch (e) { toast.error('Failed to update'); }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/invoice`, { headers: getAuthHeaders() });
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${res.data.content}`;
      link.download = res.data.filename;
      link.click();
    } catch (e) { toast.error('Failed to download invoice'); }
  };

  const statusColors = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800', processing: 'bg-purple-100 text-purple-800', shipped: 'bg-indigo-100 text-indigo-800', delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center gap-4 mb-6"><Link to="/admin" className="text-muted-foreground hover:text-gold"><ChevronLeft className="h-5 w-5" /></Link><h1 className="text-2xl font-bold text-navy">Orders Management</h1></div>
      
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Order #</TableHead><TableHead>Customer</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id}>
                <TableCell><div className="font-medium">{o.order_number}</div><div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</div></TableCell>
                <TableCell><div>{o.shipping_address?.full_name}</div><div className="text-sm text-muted-foreground">{o.shipping_address?.phone}</div></TableCell>
                <TableCell>{o.items?.length} items</TableCell>
                <TableCell className="font-bold text-gold">AED {o.total?.toFixed(2)}</TableCell>
                <TableCell><Badge className={o.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{o.payment_method === 'cod' ? 'COD' : o.payment_status}</Badge></TableCell>
                <TableCell>
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>{['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Dialog><DialogTrigger asChild><Button size="sm" variant="ghost" onClick={() => setSelectedOrder(o)}><Eye className="h-4 w-4" /></Button></DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader><DialogTitle>Order {o.order_number}</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4"><div><h4 className="font-semibold mb-2">Shipping Address</h4><p>{o.shipping_address?.full_name}<br/>{o.shipping_address?.address_line1}<br/>{o.shipping_address?.city}, {o.shipping_address?.emirate}<br/>{o.shipping_address?.phone}</p></div><div><h4 className="font-semibold mb-2">Order Details</h4><p>Status: {o.status}<br/>Payment: {o.payment_method} ({o.payment_status})<br/>Date: {new Date(o.created_at).toLocaleString()}</p></div></div>
                          <div><h4 className="font-semibold mb-2">Items</h4>{o.items?.map((item, i) => <div key={i} className="flex justify-between py-2 border-b"><span>{item.product_name} x{item.quantity}</span><span>AED {item.total_price?.toFixed(2)}</span></div>)}</div>
                          <div className="text-right space-y-1"><div>Subtotal: AED {o.subtotal?.toFixed(2)}</div><div>VAT: AED {o.vat_amount?.toFixed(2)}</div><div>Shipping: AED {o.shipping_cost?.toFixed(2)}</div><div className="text-lg font-bold text-gold">Total: AED {o.total?.toFixed(2)}</div></div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button size="sm" variant="ghost" onClick={() => downloadInvoice(o.id)}><Download className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Admin Categories Management
export const AdminCategories = () => {
  const { user, getAuthHeaders } = useApp();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', name_ar: '', description: '', image: '', icon: '', is_active: true });

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchCategories();
  }, [user, navigate]);

  const fetchCategories = async () => {
    try { const res = await axios.get(`${API}/categories/all`, { headers: getAuthHeaders() }); setCategories(res.data); } catch (e) { toast.error('Failed to load categories'); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, formData, { headers: getAuthHeaders() });
        toast.success('Category created');
      }
      setShowForm(false); setEditingCategory(null); fetchCategories();
      setFormData({ name: '', name_ar: '', description: '', image: '', icon: '', is_active: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save category'); }
  };

  const handleEdit = (cat) => {
    setEditingCategory(cat);
    setFormData({ name: cat.name, name_ar: cat.name_ar || '', description: cat.description || '', image: cat.image || '', icon: cat.icon || '', is_active: cat.is_active });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await axios.delete(`${API}/categories/${id}`, { headers: getAuthHeaders() }); toast.success('Category deleted'); fetchCategories(); } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4"><Link to="/admin" className="text-muted-foreground hover:text-gold"><ChevronLeft className="h-5 w-5" /></Link><h1 className="text-2xl font-bold text-navy">Categories Management</h1></div>
        <Button onClick={() => { setEditingCategory(null); setFormData({ name: '', name_ar: '', description: '', image: '', icon: '', is_active: true }); setShowForm(true); }} className="bg-gold text-navy-dark"><Plus className="h-4 w-4 mr-2" />Add Category</Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader><CardTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name (English) *</Label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Name (Arabic)</Label><Input value={formData.name_ar} onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })} dir="rtl" /></div>
              <div className="space-y-2 md:col-span-2"><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
              <div className="space-y-2"><Label>Image URL</Label><Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} /></div>
              <div className="space-y-2"><Label>Icon (Lucide icon name)</Label><Input value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} placeholder="e.g. Droplets, Zap" /></div>
              <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} /><Label>Active</Label></div>
              <div className="md:col-span-2 flex gap-2"><Button type="submit" className="bg-gold text-navy-dark">Save</Button><Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat) => (
          <Card key={cat.id} className="overflow-hidden">
            <div className="h-32 bg-muted">{cat.image ? <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full text-muted-foreground"><ImageIcon className="h-12 w-12" /></div>}</div>
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div><h3 className="font-semibold">{cat.name}</h3>{cat.name_ar && <p className="text-sm text-gold" dir="rtl">{cat.name_ar}</p>}</div>
                <Badge className={cat.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{cat.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{cat.product_count || 0} products</p>
              <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => handleEdit(cat)}><Edit className="h-4 w-4 mr-1" />Edit</Button><Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(cat.id)}><Trash2 className="h-4 w-4 mr-1" />Delete</Button></div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Admin Settings
export const AdminSettings = () => {
  const { user, getAuthHeaders } = useApp();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try { const res = await axios.get(`${API}/settings/admin`, { headers: getAuthHeaders() }); setSettings(res.data); } catch (e) { toast.error('Failed to load settings'); }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try { await axios.put(`${API}/settings`, settings, { headers: getAuthHeaders() }); toast.success('Settings saved successfully'); } catch (e) { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center gap-4 mb-6"><Link to="/admin" className="text-muted-foreground hover:text-gold"><ChevronLeft className="h-5 w-5" /></Link><h1 className="text-2xl font-bold text-navy">Store Settings</h1></div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Payment Gateway Toggle - Prominent Card */}
        <Card className={`lg:col-span-2 border-2 ${settings.payment_enabled ? 'border-green-500 bg-green-50/30' : 'border-orange-500 bg-orange-50/30'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-gold" />
              Payment Gateway
              <Badge className={settings.payment_enabled ? 'bg-green-500' : 'bg-orange-500'}>
                {settings.payment_enabled ? 'ENABLED' : 'DISABLED'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
              <div>
                <Label className="text-lg font-semibold">Online Payment (Stripe)</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {settings.payment_enabled 
                    ? 'Customers can pay with credit/debit cards at checkout' 
                    : 'Online payment is OFF. Orders will be sent to admin email for manual processing'}
                </p>
              </div>
              <Switch 
                checked={settings.payment_enabled} 
                onCheckedChange={(v) => setSettings({ ...settings, payment_enabled: v })}
                className="scale-125"
              />
            </div>
            
            {!settings.payment_enabled && (
              <div className="bg-orange-100 border border-orange-300 rounded-lg p-4">
                <p className="text-sm text-orange-800 font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Payment Gateway Disabled Mode
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  When customers complete checkout, their order details will be emailed to: <strong>{settings.admin_notification_email || 'ajith@lenokinfotech'}</strong>
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Admin Notification Email</Label>
              <Input 
                type="email" 
                placeholder="admin@example.com"
                value={settings.admin_notification_email || ''} 
                onChange={(e) => setSettings({ ...settings, admin_notification_email: e.target.value })} 
              />
              <p className="text-xs text-muted-foreground">Order notifications will be sent to this email when payment is disabled</p>
            </div>

            <Separator />
            
            <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
              <div>
                <Label className="text-base font-medium">Cash on Delivery (COD)</Label>
                <p className="text-sm text-muted-foreground">Allow customers to pay cash upon delivery</p>
              </div>
              <Switch checked={settings.cod_enabled} onCheckedChange={(v) => setSettings({ ...settings, cod_enabled: v })} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Percent className="h-5 w-5 text-gold" />Tax & Shipping</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>VAT Percentage (%)</Label><Input type="number" step="0.1" value={settings.vat_percentage || ''} onChange={(e) => setSettings({ ...settings, vat_percentage: parseFloat(e.target.value) })} /></div>
            <div className="space-y-2"><Label>Flat Shipping Rate (AED)</Label><Input type="number" step="0.01" value={settings.flat_shipping_rate || ''} onChange={(e) => setSettings({ ...settings, flat_shipping_rate: parseFloat(e.target.value) })} /></div>
            <div className="space-y-2"><Label>Free Shipping Threshold (AED)</Label><Input type="number" step="0.01" value={settings.free_shipping_threshold || ''} onChange={(e) => setSettings({ ...settings, free_shipping_threshold: parseFloat(e.target.value) })} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Building className="h-5 w-5 text-gold" />Company Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Company Name</Label><Input value={settings.company_name || ''} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} /></div>
            <div className="space-y-2"><Label>TRN (Tax Registration Number)</Label><Input value={settings.company_trn || ''} onChange={(e) => setSettings({ ...settings, company_trn: e.target.value })} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={settings.company_phone || ''} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={settings.company_email || ''} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} /></div>
            <div className="space-y-2"><Label>WhatsApp Number</Label><Input value={settings.whatsapp_number || ''} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} /></div>
            <div className="space-y-2"><Label>Address</Label><Textarea value={settings.company_address || ''} onChange={(e) => setSettings({ ...settings, company_address: e.target.value })} /></div>
          </CardContent>
        </Card>

        {/* Firebase Email Configuration */}
        <Card className={`lg:col-span-2 ${settings.firebase_enabled ? 'border-2 border-blue-500' : ''}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="h-5 w-5 text-orange-500" viewBox="0 0 24 24" fill="currentColor"><path d="M3.89 15.672L6.255.461A.542.542 0 017.27.288l2.543 4.771zm16.794 3.692l-2.25-14a.54.54 0 00-.919-.295L3.316 19.365l7.856 4.427a1.621 1.621 0 001.588 0zM14.3 7.147l-1.82-3.482a.542.542 0 00-.96 0L3.53 17.984z"/></svg>
              Firebase Email Configuration
              <Badge className={settings.firebase_enabled ? 'bg-blue-500' : 'bg-gray-400'}>
                {settings.firebase_enabled ? 'ENABLED' : 'DISABLED'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Use Firebase for Email OTP</Label>
                <p className="text-sm text-muted-foreground">When enabled, OTP verification emails will be sent via Firebase instead of SendGrid</p>
              </div>
              <Switch 
                checked={settings.firebase_enabled} 
                onCheckedChange={(v) => setSettings({ ...settings, firebase_enabled: v })}
              />
            </div>
            
            {settings.firebase_enabled && (
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label>Firebase API Key</Label>
                  <Input 
                    type="password"
                    placeholder="AIzaSy..."
                    value={settings.firebase_api_key || ''} 
                    onChange={(e) => setSettings({ ...settings, firebase_api_key: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Firebase Project ID</Label>
                  <Input 
                    placeholder="your-project-id"
                    value={settings.firebase_project_id || ''} 
                    onChange={(e) => setSettings({ ...settings, firebase_project_id: e.target.value })} 
                  />
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <Label>Firebase Auth Domain</Label>
                  <Input 
                    placeholder="your-project.firebaseapp.com"
                    value={settings.firebase_auth_domain || ''} 
                    onChange={(e) => setSettings({ ...settings, firebase_auth_domain: e.target.value })} 
                  />
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">
                    Get these values from your Firebase Console → Project Settings → General
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 flex justify-end"><Button onClick={handleSave} disabled={saving} className="bg-gold text-navy-dark font-bold px-8">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save Settings</Button></div>
    </div>
  );
};
